import { useState } from "react"
import { FiMenu } from "react-icons/fi"
import Content from "./Content"
import QuestionDetails from "./QuestionDetails"
import QuestionEditForm from "./QuestionEditForm"
import { resolveTaxonomyLabel } from "../../utils/subjectTaxonomy"
import {
  ProcessedQuestionContent,
  QuestionDocument,
  OcrTabType,
  PreviewSubjectOption,
} from "./types"

interface QuestionCardProps {
  document: QuestionDocument
  content: ProcessedQuestionContent
  subjects: PreviewSubjectOption[]
  index: number
  onChange: (name: string, value: any) => void
  onRevert: (index: number, originalContent: ProcessedQuestionContent) => void
  questionValidationErrors: { index: number; message: string }[]
}

const QuestionCard = ({
  document,
  content,
  subjects,
  index,
  onChange,
  onRevert,
  questionValidationErrors,
}: QuestionCardProps) => {
  const [isEdit, setIsEdit] = useState(false)
  const [activeTab, setActiveTab] = useState<OcrTabType>("question_result")
  const [originalContent, setOriginalContent] = useState<ProcessedQuestionContent>(content)

  const selectedSubject = subjects.find((item) => item.value === content.subject)

  const currentQuestionError = questionValidationErrors.find(err => err.index === index - 1)?.message

  const tabOptions: { key: OcrTabType; label: string }[] = [
    { key: "question_result", label: "question" },
    { key: "solution_result", label: "solution" },
    { key: "answer_result", label: "answer" },
  ]

  const handleTabClick = (tab: OcrTabType) => {
    setActiveTab(tab)
  }

  const handleEdit = () => {
    setOriginalContent(content) // Save current content when entering edit mode
    setIsEdit(true)
  }

  const handleSave = () => {
    setIsEdit(false)
  }

  const handleCancel = () => {
    onRevert(index - 1, originalContent) // Revert changes
    setIsEdit(false)
  }

  const getCurrentContent = () => {
    const tabKey = activeTab.split("_")[0] as keyof ProcessedQuestionContent
    return content[tabKey] as string
  }

  return (
    <div className='single_question'>
      <div className='left'>
        {/* Question Header */}
        <div className='header'>
          <div className='left_menu'>
            <FiMenu color='var(--gray-400)' fontSize={22} />
            <div className='index'>{index}</div>
            <div className='tags'>
              <div className='tag'>
                {resolveTaxonomyLabel(selectedSubject?.topics, content.topic)}
              </div>
              <div className='tag'>{content?.complexity}</div>
            </div>
          </div>

          {/* Tabs */}
          <div className='right_menu'>
            {tabOptions.map(({ key, label }) => (
              <div
                key={key}
                className={`tab ${activeTab === key ? "active" : ""}`}
                onClick={() => handleTabClick(key)}
              >
                {label}
              </div>
            ))}
          </div>
        </div>

        <Content
          elements={document[activeTab]?.data}
          isEdit={isEdit}
          activeTab={activeTab}
          currentContent={getCurrentContent()}
          handleOnChange={(tabKey: string, value: string) => {
            onChange(tabKey, value)
          }}
          index={0} // Not used anymore since we handle content directly
        />
      </div>

      {/* Right Panel */}
      {isEdit ? (
        <QuestionEditForm
          content={content}
          subjects={subjects}
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={(name: string, value: any) => onChange(name, value)}
          validationError={currentQuestionError}
        />
      ) : (
        <QuestionDetails
          content={content}
          subjects={subjects}
          onEdit={handleEdit}
        />
      )}
    </div>
  )
}

export default QuestionCard
